import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { confirmBookingFromSession } from "@/lib/booking.functions";
import { getStripeEnvironment } from "@/lib/stripe-browser";
import { Skeleton } from "@/components/ui/skeleton";
import { markRecoverySessionConvertedClient } from "@/hooks/use-recovery-intent";

export const Route = createFileRoute("/booking/return")({
  head: () => ({ meta: [{ title: "Confirmando reserva — Mostrando México" }, { name: "robots", content: "noindex,nofollow" }] }),
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: ReturnPage,
});

type State =
  | { kind: "loading" }
  | { kind: "ok"; code: string }
  | { kind: "pending"; code: string | null; source: string | null }
  | { kind: "error"; error: string };

const MAX_ATTEMPTS = 12;
const RETRY_MS = 2500;

function ReturnPage() {
  const { t } = useTranslation();
  const { session_id: sessionId } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "error", error: "missing_session" });
      return;
    }
    let cancelled = false;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS && !cancelled; attempt++) {
        try {
          const res = await confirmBookingFromSession({
            data: { sessionId, environment: getStripeEnvironment() },
          });
          if (cancelled) return;
          if (res.ok && res.bookingCode) {
            setState({ kind: "ok", code: res.bookingCode });
            // Cierra las oportunidades abiertas de este navegador (no hay pérdida).
            markRecoverySessionConvertedClient();
            setTimeout(() => navigate({ to: "/booking/$code", params: { code: res.bookingCode! } }), 800);
            return;
          }
          // Payment captured, reservation still finalizing → keep polling with a
          // reassuring (non-error) message worded for the booking's source.
          if (res.pending) {
            setState({ kind: "pending", code: res.bookingCode ?? null, source: res.source ?? null });
            if (attempt === MAX_ATTEMPTS) return;
            await sleep(RETRY_MS);
            continue;
          }
          setState({ kind: "error", error: res.error ?? t("bookingFlow.ret.errorTitle") });
          return;
        } catch (e) {
          if (cancelled) return;
          if (attempt === MAX_ATTEMPTS) {
            setState({ kind: "error", error: (e as Error).message });
            return;
          }
          await sleep(RETRY_MS);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, navigate, t]);

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-xl px-6 py-16 text-center">
        {state.kind === "loading" && (
          <>
            <h1 className="text-xl font-semibold">{t("bookingFlow.ret.confirming")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("bookingFlow.ret.verifying")}</p>
            <div className="mt-6"><Skeleton className="mx-auto h-32 w-full max-w-sm" /></div>
          </>
        )}
        {state.kind === "ok" && (
          <>
            <h1 className="text-xl font-semibold">{t("bookingFlow.ret.confirmed")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("bookingFlow.ret.redirecting")}</p>
          </>
        )}
        {state.kind === "pending" && (
          <>
            <h1 className="text-xl font-semibold">{t("bookingFlow.ret.paid")} ✅</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {state.source === "bokun"
                ? t("bookingFlow.ret.syncBokun")
                : t("bookingFlow.ret.paidNote")}
            </p>
            {state.code && (
              <p className="mt-2 text-sm">
                {t("bookingFlow.ret.reference")}: <span className="font-mono font-semibold">{state.code}</span>
              </p>
            )}
            <div className="mt-6"><Skeleton className="mx-auto h-24 w-full max-w-sm" /></div>
            {state.code && (
              <Link
                to="/booking/$code"
                params={{ code: state.code }}
                className="mt-6 inline-block text-sm underline"
              >
                {t("bookingFlow.ret.viewStatus")}
              </Link>
            )}
          </>
        )}
        {state.kind === "error" && (
          <>
            <h1 className="text-xl font-semibold">{t("bookingFlow.ret.errorTitle")}</h1>
            <p className="mt-2 text-sm text-destructive">
              {state.error === "missing_session" ? t("bookingFlow.ret.missingSession") : state.error}
            </p>
            <p className="mt-4 text-sm text-muted-foreground">{t("bookingFlow.ret.errorHelp")}</p>
            <Link to="/" className="mt-6 inline-block text-sm underline">{t("bookingFlow.ret.home")}</Link>
          </>
        )}
      </main>
    </div>
  );
}
